import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { getAuth, sendEmailVerification, signOut } from 'firebase/auth';
import { EnvelopeIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

import app from '../utils/firebaseApp.js';
import { AuthContext } from '../utils/auth.js';
import { consumeIntendedPath } from '../utils/authRedirect.js';
import AuthLayout from '../components/layout/AuthLayout.js';
import { Button, notify } from '../ui/index.js';
import useDocumentTitle from '../hooks/useDocumentTitle.js';

const RESEND_COOLDOWN = 30;

export default function EmailVerificationNotice() {
  useDocumentTitle('Verify your email');
  const history = useHistory();
  const { currentUser, refreshUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  // Poll for verification so the user does not have to do anything after
  // clicking the link in the email.
  useEffect(() => {
    let active = true;
    const interval = setInterval(async () => {
      try {
        const user = await refreshUser();
        if (active && user?.emailVerified) {
          clearInterval(interval);
          notify.success('Email verified. Welcome!');
          history.replace(consumeIntendedPath());
        }
      } catch (error) {
        // ignore transient errors
      }
    }, 3000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [refreshUser, history]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleResend = async () => {
    const user = getAuth(app).currentUser;
    if (!user) return;
    setLoading(true);
    try {
      await sendEmailVerification(user);
      notify.success('Verification email sent. Check your inbox.');
      setCooldown(RESEND_COOLDOWN);
    } catch (error) {
      notify.error(error?.code === 'auth/too-many-requests' ? 'Too many requests. Please wait a few minutes and try again.' : 'Could not send the email. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckNow = async () => {
    setChecking(true);
    try {
      const user = await refreshUser();
      if (user?.emailVerified) {
        history.replace(consumeIntendedPath());
      } else {
        notify.info('Not verified yet. Click the link in the email, then try again.');
      }
    } finally {
      setChecking(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await signOut(getAuth(app));
      history.replace('/login');
    } catch (error) {
      notify.error('Could not sign out. Please try again.');
    }
  };

  return (
    <AuthLayout
      title="Verify your email"
      footer={
        <>
          Wrong account?{' '}
          <button type="button" onClick={handleDisconnect} className="font-semibold text-indigo-600 hover:text-indigo-500">
            Sign out
          </button>
        </>
      }
    >
      <div className="text-center">
        <span className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600">
          <EnvelopeIcon className="h-6 w-6" aria-hidden="true" />
        </span>
        <p className="mt-4 text-sm text-gray-600">
          We sent a verification link to <span className="font-medium text-gray-900">{currentUser?.email}</span>. Open it to activate your account. This page updates automatically.
        </p>
      </div>

      <div className="mt-6 flex flex-col gap-2">
        <Button size="lg" block onClick={handleCheckNow} loading={checking} icon={ArrowPathIcon}>
          I have verified my email
        </Button>
        <Button variant="secondary" size="lg" block onClick={handleResend} loading={loading} disabled={cooldown > 0}>
          {cooldown > 0 ? `Resend available in ${cooldown}s` : 'Resend verification email'}
        </Button>
      </div>

      <p className="mt-5 text-center text-xs text-gray-400">Can’t find it? Check your spam folder or wait a minute and resend.</p>
    </AuthLayout>
  );
}
